import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Tooltip } from "@mui/material";

const defaultReactions = [
  { type: "copy" },
  { type: "like" },
  { type: "dislike" },
];

const iconMap = {
  copy: "/assets/images/copy.svg",
  copied: "/assets/images/copied.svg",
  like: "/assets/images/select_icon.svg",
  dislike: "/assets/images/unselect_icon.svg",
  selected: "/assets/images/selected_icon.svg",
  unselected: "/assets/images/unselected_icon.svg",
};

const useStyles = () => ({
  container: {
    display: "flex",
    gap: 2.5,
    alignItems: "center",
    mt: 0,
  },
  iconButton: {
    width: 40,
    height: 40,
    padding: 0,
  },
  icon: {
    width: 24,
    height: 24,
  },
  copyIcon: {
    width: 24,
    height: 24,
  },
  tooltipPopper: {
    zIndex: 9999,
  },
  tooltip: {
    borderRadius: '4px',
    background: '#2E3131',
    color: '#FFF',
    fontFamily: 'Montserrat',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
  },
  copyTextarea: {
    position: "absolute",
    left: "-9999px",
  },
});

export function Reactions({ reactions, handleReactionEvent, messageText }) {
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const styles = useStyles();

  const reactionTypes = useMemo(
    () => {
      const incoming = reactions?.length ? reactions : defaultReactions;
      const hasCopy = incoming.some((reaction) => reaction.type === "copy");
      return hasCopy ? incoming : [{ type: "copy" }, ...incoming];
    },
    [reactions]
  );

  const sanitizedCopyText = useMemo(() => {
    if (typeof messageText !== "string") return "";
    return messageText.replace(/<[^>]*>/g, "");
  }, [messageText]);

  const handleCopy = async () => {
    setCopied(true);
    handleReactionEvent("copy");

    if (!sanitizedCopyText) return;
    try {
      await navigator.clipboard.writeText(sanitizedCopyText);
    } catch (error) {
      console.error("Copy failed", error);
    }

    // Reset copied state after 800ms
    setTimeout(() => {
      setCopied(false);
    }, 800);
  };

  const handleReaction = (type) => {
    if (type === "copy") {
      handleCopy();
      return;
    }

    // Toggle selection: if already selected, unselect it
    setSelected(selected === type ? null : type);
    setCopied(false);
    handleReactionEvent(type);
  };

  return (
    <Box sx={styles.container}>
      {reactionTypes.map((reaction) => {
        const type = reaction.type;
        
        if (type === "copy") {
          const iconSrc = copied ? iconMap.copied : iconMap.copy;
          return (
            <Tooltip 
              key={type} 
              title="Copy" 
              placement="bottom" 
              slotProps={{ 
                popper: { 
                  sx: styles.tooltipPopper
                },
                tooltip: {
                  sx: styles.tooltip
                }
              }}
            >
              <IconButton
                onClick={() => handleReaction(type)}
                size="small"
                aria-label={type}
                sx={styles.iconButton}
              >
                <img src={iconSrc} alt={type} style={copied ? styles.icon : styles.copyIcon} />
              </IconButton>
            </Tooltip>
          );
        }

        if (type === "like") {
          // Show selected icon and hide dislike when like is selected
          if (selected === "like") {
            return (
              <Tooltip 
                key={type} 
                title="Helpful" 
                placement="bottom" 
                slotProps={{ 
                  popper: { 
                    sx: styles.tooltipPopper
                  },
                  tooltip: {
                    sx: styles.tooltip
                  }
                }}
              >
                <IconButton
                  onClick={() => handleReaction(type)}
                  size="small"
                  aria-label={type}
                  sx={styles.iconButton}
                >
                  <img src={iconMap.selected} alt="like" style={styles.icon} />
                </IconButton>
              </Tooltip>
            );
          }
          // Show like icon only if dislike is not selected
          if (selected !== "dislike") {
            return (
              <Tooltip 
                key={type} 
                title="Helpful" 
                placement="bottom" 
                slotProps={{ 
                  popper: { 
                    sx: styles.tooltipPopper
                  },
                  tooltip: {
                    sx: styles.tooltip
                  }
                }}
              >
                <IconButton
                  onClick={() => handleReaction(type)}
                  size="small"
                  aria-label={type}
                  sx={styles.iconButton}
                >
                  <img src={iconMap.like} alt="like" style={styles.icon} />
                </IconButton>
              </Tooltip>
            );
          }
          return null;
        }

        if (type === "dislike") {
          // Show unselected icon and hide like when dislike is selected
          if (selected === "dislike") {
            return (
              <Tooltip 
                key={type} 
                title="Not helpful" 
                placement="bottom" 
                slotProps={{ 
                  popper: { 
                    //modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
                    sx: styles.tooltipPopper
                  },
                  tooltip: {
                    sx: styles.tooltip
                  }
                }}
              >
                <IconButton
                  onClick={() => handleReaction(type)}
                  size="small"
                  aria-label={type}
                  sx={styles.iconButton}
                >
                  <img src={iconMap.unselected} alt="dislike" style={styles.icon} />
                </IconButton>
              </Tooltip>
            );
          }
          // Show dislike icon only if like is not selected
          if (selected !== "like") {
            return (
              <Tooltip 
                key={type} 
                title="Not helpful" 
                placement="bottom" 
                slotProps={{ 
                  popper: { 
                    sx: styles.tooltipPopper
                  },
                  tooltip: {
                    sx: styles.tooltip
                  }
                }}
              >
                <IconButton
                  onClick={() => handleReaction(type)}
                  size="small"
                  aria-label={type}
                  sx={styles.iconButton}
                >
                  <img src={iconMap.dislike} alt="dislike" style={styles.icon} />
                </IconButton>
              </Tooltip>
            );
          }
          return null;
        }

        return null;
      })}
    </Box>
  );
}

Reactions.propTypes = {
  reactions: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
    })
  ),
  handleReactionEvent: PropTypes.func.isRequired,
  messageText: PropTypes.string,
};
